/**
 * @file error.interceptor.ts
 * @description this file implements interceptor to handler http errors
 */

import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService, MultilingualService } from '@core/services';

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private readonly authService: AuthService,
    private readonly multilingualService: MultilingualService
  ) {}

  /**
   * @description Interceptor to handle http errors
   * @param  HttpRequest request
   * @param  HttpHandler next
   * @returns Observable
   */
  public intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    let errMsg: string;

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleUnauthorized(error);
        errMsg =
          this.handleTooManyRequests(error, errMsg) ??
          this.handleServerOrBlobError(error, errMsg) ??
          this.extractMessageFromJsonString(error, errMsg) ??
          this.extractMessageFromStringPayload(error, errMsg) ??
          this.extractMessageFromObjectPayload(error, errMsg);

        return throwError(() => errMsg);
      })
    );
  }

  /**
   * Handles 401 Unauthorized responses by logging out the user.
   * @param error HttpErrorResponse
   */
  private handleUnauthorized(error: HttpErrorResponse): void {
    if (error.status === 401) {
      this.authService.logout();
    }
  }

  /**
   * Handles 429 Too Many Requests by setting a localized error message.
   * @param error HttpErrorResponse
   * @param errMsg Current error message accumulator
   * @returns Updated error message
   */
  private handleTooManyRequests(error: HttpErrorResponse, errMsg: string): string {
    if (error.status === 429) {
      errMsg = this.multilingualService.langMap.ERROR_COMMON_TOO_MANY_ATTEMPTS;
    }
    return errMsg;
  }

  /**
   * Handles generic server-side errors (5xx) or Blob payloads by setting a generic message.
   * NOTE: Keeps the original condition grouping unchanged.
   * @param error HttpErrorResponse
   * @param errMsg Current error message accumulator
   * @returns Updated error message
   */
  private handleServerOrBlobError(error: HttpErrorResponse, errMsg: string): string {
    if (
      error.status >= 500 ||
      error.status >= 503 ||
      error.status >= 502 ||
      error.error instanceof Blob
    ) {
      errMsg = this.multilingualService.langMap.ERROR_COMMON_SOMETHING_WENT_WRONG;
    }
    return errMsg;
  }

  /**
   * Attempts to parse a JSON string payload and extract a meaningful message.
   * Expects a string containing '{' to indicate potential JSON.
   * @param error HttpErrorResponse
   * @param errMsg Current error message accumulator
   * @returns Updated error message
   */
  private extractMessageFromJsonString(error: HttpErrorResponse, errMsg: string): string {
    if (typeof error.error === 'string' && error.error.includes('{')) {
      try {
        const errorDetails = JSON.parse(error.error);
        if (errorDetails.message) {
          errMsg = errorDetails.message;
        } else if (errorDetails.errorMessage) {
          errMsg = errorDetails.errorMessage;
        } else {
          errMsg = this.multilingualService.langMap.ERROR_COMMON_SOMETHING_WENT_WRONG;
        }
      } catch (e) {
        errMsg = this.multilingualService.langMap.ERROR_COMMON_SOMETHING_WENT_WRONG;
      }
    }
    return errMsg;
  }

  /**
   * Uses a plain string payload as the error message when present.
   * @param error HttpErrorResponse
   * @param errMsg Current error message accumulator
   * @returns Updated error message
   */
  private extractMessageFromStringPayload(error: HttpErrorResponse, errMsg: string): string {
    if (typeof error.error === 'string') {
      errMsg = error.error;
    }
    return errMsg;
  }

  /**
   * Extracts message fields from an object payload (e.g., { message } or { errorMessage }).
   * Falls back to a generic message if no known fields are present or payload is not an object.
   * @param error HttpErrorResponse
   * @param errMsg Current error message accumulator
   * @returns Updated error message
   */
  private extractMessageFromObjectPayload(error: HttpErrorResponse, errMsg: string): string {
    if (typeof error.error === 'object') {
      if (error.error?.message) {
        errMsg = error.error.message;
      } else if (error.error?.errorMessage) {
        errMsg = error.error.errorMessage;
      } else {
        errMsg = this.multilingualService.langMap.ERROR_COMMON_SOMETHING_WENT_WRONG;
      }
    } else {
      errMsg = this.multilingualService.langMap.ERROR_COMMON_SOMETHING_WENT_WRONG;
    }
    return errMsg;
  }
}
